import React, { useMemo, useState } from 'react';
import { Col, Input, Label, Row } from 'reactstrap';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import CustomSelection from './CustomSelection';
import htmlToDraft from 'html-to-draftjs';

const formatDate = date => {
  return date?.split('T')[0];
};

const ReleaseNote = ({ releaseFormValues, currentState, edit }) => {
  const typeOptions = ['Maintenance Notification', 'Release Notes'];
  const frequencyOptions = ['One Time', 'Daily', 'Weekly', 'Monthly'];
  const weekDaysOptions = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const weeksCount = ['First', 'Second', 'Third', 'Fourth'];
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isInitialState, setIsInitialState] = useState(true);
  const [recFrequency, setRecFrequency] = useState(frequencyOptions[0]);
  const [formValues, setFormValues] = useState({
    type: typeOptions[0],
    title: '',
    alertStartsOn: '',
    startTime: '',
    alertExpiresOn: '',
    expireTime: '',
    htmlText: '',
    frequency: recFrequency,
    recFrequency: {
      settings: {
        weekDays: [],
      },
    },
  });

  if (currentState && isInitialState) {
    setRecFrequency(currentState?.frequency);
    setFormValues({
      ...currentState,
      alertExpiresOn: formatDate(currentState?.alertExpiresOn),
      alertStartsOn: formatDate(currentState?.alertStartsOn),
      recFrequency: {
        settings: {
          ...currentState?.dailySettings,
          ...currentState?.weeklySettings,
          ...currentState?.monthlySettings,
        },
      },
    });
    setIsInitialState(false);
    if (edit) {
      const contentBlock = htmlToDraft(currentState.htmlText);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setEditorState(EditorState.createWithContent(contentState));
      }
    } else {
      const content = ContentState.createFromText(currentState.htmlText);
      setEditorState(EditorState.createWithContent(content));
    }
  }

  const onEditorStateChange = e => {
    setEditorState(e);
    const htmlText =
      formValues.type === typeOptions[0]
        ? convertToRaw(e.getCurrentContent()).blocks[0].text
        : draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setFormValues({ ...formValues, htmlText });
  };

  useMemo(() => {
    releaseFormValues(formValues);
  }, [formValues]);

  const onChangeValues = e => {
    const { name, value } = e.target;
    if (formValues.type === typeOptions[0]) {
      if (
        !Object.hasOwn(formValues, 'alertExpiresOn') ||
        !Object.hasOwn(formValues, 'expireTime')
      ) {
        formValues[name] = value;
      }
      if (recFrequency !== 'Weekly') delete formValues?.recFrequency?.settings?.weekDays;
      if (recFrequency === frequencyOptions[0]) delete formValues?.recFrequency?.settings;
      const maintainObj = { publishDate: 'alertStartsOn', publishTime: 'startTime' };
      const updatedFormValues = Object.entries(formValues).reduce((op, [key, val]) => {
        const newKey = maintainObj[key];
        op[newKey || key] = val;
        return op;
      }, {});
      setFormValues({ ...updatedFormValues, [name]: value });
    } else {
      const maintainObj = { alertStartsOn: 'publishDate', startTime: 'publishTime' };
      const updatedFormValues = Object.entries(formValues).reduce((op, [key, val]) => {
        const newKey = maintainObj[key];
        op[newKey || key] = val;
        return op;
      }, {});
      setFormValues({ ...updatedFormValues, [name]: value });
    }
  };

  const onTypeChange = type => {
    const { name, value } = type.target;
    if (value === typeOptions[0]) {
      if (!Object.hasOwn(formValues, 'frequency')) {
        setRecFrequency(value);
        formValues.recFrequency = {
          settings: {
            weekDays: [],
          },
        };
      }
    } else {
      delete formValues.alertExpiresOn;
      delete formValues.expireTime;
      delete formValues.recFrequency;
    }

    setFormValues({ ...formValues, [name]: value });
  };

  const onChangeFrequency = freq => {
    const { name, value, type } = freq.target;

    if (!value) return;

    if (frequencyOptions.includes(value)) {
      formValues.recFrequency.settings = {
        weekDays: [],
      };
    }

    if (formValues.recFrequency.settings) {
      const { settings } = formValues?.recFrequency;
      if (Object.hasOwn(settings, name) && type === 'radio')
        formValues.recFrequency.settings = {
          weekDays: [],
        };
    }

    if (formValues.type === typeOptions[0]) {
      if (!Object.hasOwn(formValues, 'frequency')) {
        formValues[name] = value;
      }

      if (name === 'frequency') {
        formValues.frequency = value;
        formValues.recFrequency.settings = {
          ...currentState?.dailySettings,
          ...currentState?.weeklySettings,
          ...currentState?.monthlySettings,
        };
        if (value !== 'Weekly') delete formValues?.recFrequency?.settings?.weekDays;
        if (value === frequencyOptions[0]) delete formValues?.recFrequency?.settings;
        setRecFrequency(value);
      } else if (weekDaysOptions.includes(value) && recFrequency === 'Weekly') {
        if (!formValues?.recFrequency?.settings?.weekDays) {
          formValues.recFrequency.settings = {
            ...formValues.recFrequency.settings,
            weekDays: [],
          };
        }
        if (formValues?.recFrequency?.settings?.weekDays?.includes(value)) {
          formValues.recFrequency.settings.weekDays = formValues.recFrequency.settings.weekDays.filter(
            e => e !== value,
          );
        } else {
          formValues.recFrequency.settings.weekDays.push(value);
        }
      } else {
        formValues.recFrequency.settings[name] = value;
        if (recFrequency !== 'Weekly') delete formValues?.recFrequency?.settings?.weekDays;
      }
    }
  };

  return (
    <>
      <Row>
        <Col md={formValues.type === typeOptions[1] ? 6 : 4} sm={12}>
          <Row className="margin-col">
            <Col md={3}>
              <h1>Type:</h1>
            </Col>
            <Col md={8}>
              <CustomSelection
                disabled={!isInitialState}
                name="type"
                onChange={onTypeChange}
                optionsList={typeOptions}
              />
            </Col>
          </Row>
          <Row className="margin-col">
            <Col md={3}>
              <h1>Title:</h1>
            </Col>
            <Col md={8}>
              <Input
                placeholder="Please specify"
                required
                type="text"
                name="title"
                value={formValues.title}
                onChange={onChangeValues}
              />
            </Col>
          </Row>
        </Col>
        <Col md={formValues.type === typeOptions[1] ? 6 : 4} sm={12}>
          <Row className="margin-col">
            <Col md={3}>
              <h1>{formValues.type === typeOptions[1] ? 'Publish Date' : 'Start Date'}:</h1>
            </Col>
            <Col md={8}>
              <Input
                placeholder="Please specify"
                required
                type="date"
                name={formValues.type === typeOptions[1] ? 'publishDate' : 'alertStartsOn'}
                value={
                  formValues.type === typeOptions[1]
                    ? formValues.publishDate
                    : formValues.alertStartsOn && formatDate(formValues.alertStartsOn)
                }
                onChange={onChangeValues}
              />
            </Col>
          </Row>
          <Row className="margin-col">
            <Col md={3}>
              <h1>{formValues.type === typeOptions[1] ? 'Publish Time' : 'Start Time'}:</h1>
            </Col>
            <Col md={8}>
              <Input
                placeholder="Please specify"
                required
                type="time"
                name={formValues.type === typeOptions[1] ? 'publishTime' : 'startTime'}
                value={
                  formValues.type === typeOptions[1] ? formValues.publishTime : formValues.startTime
                }
                onChange={onChangeValues}
              />
            </Col>
          </Row>
        </Col>
        {formValues.type === typeOptions[0] && (
          <Col md={4} sm={12}>
            <Row className="margin-col">
              <Col md={3}>
                <h1>End Date:</h1>
              </Col>
              <Col md={8}>
                <Input
                  placeholder="Please specify"
                  required
                  type="date"
                  name="alertExpiresOn"
                  value={formValues?.alertExpiresOn && formatDate(formValues.alertExpiresOn)}
                  onChange={onChangeValues}
                />
              </Col>
            </Row>
            <Row className="margin-col">
              <Col md={3}>
                <h1>End Time:</h1>
              </Col>
              <Col md={8}>
                <Input
                  placeholder="Please specify"
                  required
                  type="time"
                  name="expireTime"
                  value={formValues?.expireTime}
                  onChange={onChangeValues}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
      {formValues.type === typeOptions[0] && (
        <Row>
          <Col md={4} sm={12}>
            <Row className="margin-col">
              <Col md={3}>
                <h1>Frequency:</h1>
              </Col>
              <Col md={8}>
                <CustomSelection
                  disabled={!isInitialState}
                  name="frequency"
                  onChange={onChangeFrequency}
                  optionsList={frequencyOptions}
                  value={currentState?.frequency}
                />
              </Col>
            </Row>
          </Col>
          <Col md={8} sm={12} className="recFrequency">
            {recFrequency === 'Daily' ? (
              <>
                <Label check sm={6} className="recFrequencyDaily">
                  <Input
                    type="radio"
                    name="frequencyDaily"
                    className="everyDayOption"
                    onChange={onChangeFrequency}
                    defaultValue="selectedDay"
                    defaultChecked={currentState?.dailySettings?.frequencyDaily === 'selectedDay'}
                  />
                  Every
                  <Input
                    type="number"
                    name="freqDailyCount"
                    onChange={onChangeFrequency}
                    defaultValue={currentState?.dailySettings?.freqDailyCount}
                  />
                  day(s)
                </Label>
                <Label check sm={6}>
                  <Input
                    type="radio"
                    name="frequencyDaily"
                    onChange={onChangeFrequency}
                    defaultValue="everyDay"
                    defaultChecked={currentState?.dailySettings?.frequencyDaily === 'everyDay'}
                  />
                  Every weekday
                </Label>
              </>
            ) : (
              ``
            )}
            {recFrequency === 'Weekly' ? (
              <>
                <Label check sm={6} className="recFrequencyWeeklyCount">
                  Every
                  <Input
                    type="number"
                    name="weekDayCount"
                    className="weeklyCount"
                    onChange={onChangeFrequency}
                    defaultValue={currentState?.weeklySettings?.weekDayCount}
                  />
                  week(s) on:
                </Label>
                <div className="recFrequencyWeekly">
                  {weekDaysOptions.map(el => (
                    <Label check sm={6}>
                      <Input
                        type="checkbox"
                        name={el}
                        onChange={onChangeFrequency}
                        value={el}
                        defaultChecked={currentState?.weeklySettings?.weekDays?.includes(el)}
                      />
                      {el}
                    </Label>
                  ))}
                </div>
              </>
            ) : (
              ``
            )}
            {recFrequency === 'Monthly' ? (
              <Col>
                <div className="recFrequencyMonthlyMain">
                  <Label check sm={6} className="recFrequencyMonthly">
                    <Input
                      type="radio"
                      name="frequencyMonthly"
                      className="everyDayOption"
                      onChange={onChangeFrequency}
                      value="monthlyOptOne"
                      defaultChecked={
                        currentState?.monthlySettings?.frequencyMonthly === 'monthlyOptOne'
                      }
                    />
                    Day
                    <Input
                      type="number"
                      name="freqMonthDailyCount"
                      className="monthlyCount"
                      onChange={onChangeFrequency}
                      defaultValue={currentState?.monthlySettings?.freqMonthDailyCount}
                    />
                    of every
                    <Input
                      type="number"
                      name="freqMonthlyCount"
                      className="monthlyCount"
                      onChange={onChangeFrequency}
                      defaultValue={currentState?.monthlySettings?.freqMonthlyCount}
                    />
                    month(s)
                  </Label>
                  <Label check sm={6} className="recFrequencyMonthly">
                    <Input
                      type="radio"
                      name="frequencyMonthly"
                      onChange={onChangeFrequency}
                      value="monthlyOptTwo"
                      defaultChecked={
                        currentState?.monthlySettings?.frequencyMonthly === 'monthlyOptTwo'
                      }
                    />
                    The
                    <CustomSelection
                      name="monthCountWeeks"
                      onChange={onChangeFrequency}
                      optionsList={weeksCount}
                      customClass="monthCountWeekOpt"
                      value={currentState?.monthlySettings?.monthCountWeeks}
                      defaultOpt
                    />
                    <CustomSelection
                      name="monthCountDays"
                      onChange={onChangeFrequency}
                      optionsList={weekDaysOptions}
                      customClass="monthCountDaysOpt"
                      value={currentState?.monthlySettings?.monthCountDays}
                      defaultOpt
                    />
                    of every
                    <Input
                      type="number"
                      name="freqMonthlyDaysCount"
                      className="monthlyCount"
                      onChange={onChangeFrequency}
                      defaultValue={currentState?.monthlySettings?.freqMonthlyDaysCount}
                    />
                    month(s)
                  </Label>
                </div>
              </Col>
            ) : (
              ``
            )}
          </Col>
        </Row>
      )}
      <Row>
        <Col md={12}>
          <Editor
            editorState={editorState}
            toolbarClassName="toolbarClassName"
            wrapperClassName="wrapperClassName"
            editorClassName="editor-class"
            onEditorStateChange={onEditorStateChange}
            toolbar={{
              options: [
                'inline',
                'blockType',
                'fontSize',
                'fontFamily',
                'list',
                'textAlign',
                'colorPicker',
                'link',
                'emoji',
                'remove',
                'history',
              ],
              inline: { inDropdown: true },
              list: { inDropdown: true },
              textAlign: { inDropdown: true },
              link: { inDropdown: true },
              history: { inDropdown: true },
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default ReleaseNote;
